import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Zap, Package, TrendingUp, IndianRupee, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// 2026-07: In-app top-up UI. Renders the catalogue from
// /api/billing/scan-packs and opens Razorpay Checkout for a one-time payment.
// On successful capture, the webhook credits billing.bonus_scans and the
// parent BillingSection re-fetches /billing/status to reflect the new cap.

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

const ICONS = { pack_25: Zap, pack_100: Package };

export function ScanPacksCard({ status, onPurchased }) {
    const [packs, setPacks] = useState(null);
    const [buying, setBuying] = useState(null); // pack_code currently in flight

    useEffect(() => {
        api.listScanPacks()
            .then((r) => setPacks(r.packs || []))
            .catch(() => setPacks([]));
    }, []);

    // Unlimited users don't need top-ups.
    if (status?.unlimited_scans) return null;

    async function buy(pack) {
        if (buying) return;
        setBuying(pack.code);
        try {
            const loaded = await loadRazorpayScript();
            if (!loaded) throw new Error('Could not load Razorpay Checkout');
            const order = await api.purchaseScanPack(pack.code);
            const rzp = new window.Razorpay({
                key: order.key_id,
                order_id: order.order_id,
                amount: order.amount_paise,
                currency: order.currency,
                name: 'Ease My Bill',
                description: `${pack.scans} bill scans top-up`,
                prefill: {
                    email: status?.email,
                },
                notes: {
                    pack_code: pack.code,
                    scans: pack.scans,
                },
                theme: { color: '#6366f1' },
                handler: function () {
                    // Payment succeeded from the user's perspective. The actual
                    // scan credit happens on the webhook side (server verifies
                    // signature + adds to bonus_scans). Poll billing/status
                    // once so the UI updates within a couple of seconds.
                    toast.success(
                        `Payment successful! ${pack.scans} scans will appear in your workspace within a few seconds.`,
                        { duration: 8000 },
                    );
                    setTimeout(() => onPurchased?.(), 2500);
                    setTimeout(() => onPurchased?.(), 6000);
                },
                modal: {
                    ondismiss: () => setBuying(null),
                },
            });
            rzp.on('payment.failed', (resp) => {
                toast.error(`Payment failed: ${resp?.error?.description || 'unknown error'}`);
                setBuying(null);
            });
            rzp.open();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Could not start payment');
            setBuying(null);
        }
    }

    if (packs === null) {
        return (
            <Card className="p-5" data-testid="scan-packs-card">
                <Skeleton className="h-6 w-40 mb-4" />
                <div className="grid sm:grid-cols-2 gap-3">
                    <Skeleton className="h-32" />
                    <Skeleton className="h-32" />
                </div>
            </Card>
        );
    }
    if (packs.length === 0) return null;

    return (
        <Card className="p-5" data-testid="scan-packs-card">
            <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    <Sparkles size={18} />
                </div>
                <div className="min-w-0">
                    <div className="font-heading text-base leading-tight">Top up your scan quota</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        One-time purchase. Bonus scans stack on top of your monthly cap and never expire.
                    </div>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                {packs.map((p) => {
                    const Icon = ICONS[p.code] || Package;
                    const isFeatured = p.code === 'pack_100';
                    const inFlight = buying === p.code;
                    return (
                        <div
                            key={p.code}
                            className={`relative rounded-xl border p-4 ${isFeatured ? 'border-primary border-2 bg-primary/[0.03]' : 'border-border/60 bg-background'}`}
                            data-testid={`scan-pack-card-${p.code}`}
                        >
                            {isFeatured && (
                                <div className="absolute -top-2 left-4 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5">
                                    <TrendingUp size={9} /> Best value
                                </div>
                            )}
                            <div className="flex items-start gap-3 mb-3">
                                <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${isFeatured ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}>
                                    <Icon size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-heading text-lg leading-tight">{p.scans} scans</div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {(p.per_scan_paise / 100).toFixed(2)} per scan
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="flex items-baseline gap-0.5">
                                        <IndianRupee size={12} />
                                        <span className="font-heading text-xl">{(p.amount_paise / 100).toFixed(0)}</span>
                                    </div>
                                </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground mb-3 min-h-[32px]">
                                {p.description}
                            </p>
                            <Button
                                variant={isFeatured ? 'default' : 'outline'}
                                className="w-full"
                                size="sm"
                                onClick={() => buy(p)}
                                disabled={!!buying}
                                data-testid={`scan-pack-buy-${p.code}`}
                            >
                                {inFlight ? 'Opening…' : `Buy ${p.scans} scans`}
                            </Button>
                        </div>
                    );
                })}
            </div>

            {status?.bonus_scans > 0 && (
                <div className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
                    <Badge variant="outline" className="gap-1">
                        <Sparkles size={10} /> {status.bonus_scans} bonus scans
                    </Badge>
                    <span>available on top of this month&apos;s cap.</span>
                </div>
            )}
        </Card>
    );
}
